<template>
  <div class="px-4">
    <div class="max-w-2xl my-10 mx-auto">
      <div class="bg-white shadow p-10 rounded w-full">
        <div class="flex items-center justify-center">
          <div
            class="flex items-center py-2"
            v-if="company && company.logo && company.logo_url"
          >
            <img
              class="h-12 w-auto"
              @error="company.logo = false"
              style="object-fit: scale-down; min-height: 100%"
              :src="company.logo_url"
              :alt="company.name"
            />
          </div>
          <div
            v-else
            class="h-16 w-16 flex rounded-full items-center justify-center"
          >
            <img
              class="h-14 w-14"
              :src="'/images/security.svg'"
              alt=""
            />
          </div>
        </div>

        <h1 class="text-center text-lg font-bold mt-8 text-blue-900">Enter the authentication code</h1>

        <div class="max-w-md mx-auto">
          <div class="pt-2 text-gray-600 text-center max-w-md mx-auto text-base">
            Protecting your data is our top priority. Please confirm your account by entering the authentication code
            sent via sms to
          </div>

          <div class="text-center mt-2">
            <div class="flex-grow">
              <div
                v-if="phone"
                class="text-gray-600 text-base font-semibold"
              >
                {{ getPhoneNumber() }}
              </div>
            </div>
          </div>
        </div>

        <form
          action="#"
          @submit.prevent="submit"
        >
          <div class="flex mt-4 items-center pt-4 justify-center font-semibold">
            <div class="flex-shrink-0">
              <otp-field
                @paste="onPaste"
                v-model="form.code"
              />
            </div>
          </div>
          <div
            class="pt-4"
            v-if="form.errors.has('code') || error"
          >
            <p class="text-red-500 whitespace-normal text-sm text-center">
              {{ form.errors.has("code") ? form.errors.first("code") : error }}
            </p>
          </div>

          <div class="mt-3 flex flex-col items-center">
            <div class="flex">
              <input
                type="checkbox"
                id="remember-me"
                class="mt-1 space-x-2"
                v-model="form.remember"
              />
              <label
                for="remember-me"
                class="font-medium ml-2"
                >Remember this device for 30 days</label
              >
            </div>

            <small class="text-gray-500">Use this option only on private devices that you trust</small>
          </div>

          <div class="flex my-8 items-center justify-center text-sm">
            <div class="flex-shrink-0">
              <v-button
                :loading="loading"
                @click.prevent="submit"
                class="bg-primary-600 hover:bg-primary-500 text-white px-12 h-12"
              >
                <template
                  #title
                  v-if="!loading"
                  >Access Secure Portal</template
                >
                <template
                  #icon
                  v-if="!loading"
                  class="icon"
                >
                  <ion-icon name="arrow-forward-outline"></ion-icon>
                </template>
              </v-button>
            </div>
          </div>

          <div
            class="text-gray-600 text-center mt-4 text-xs"
            v-if="!resent"
          >
            <div class="flex-grow">
              <span class="text-gray-500">Haven't received it yet?</span>
              <a-link
                href="#"
                class="mt-2 inline-block font-normal underline"
                @click.prevent="send(true)"
              >
                <span class="flex items-center">
                  <client-spinner
                    v-if="sending"
                    class="mr-2"
                  ></client-spinner>
                  <span class="font-normal">Resend a new code.</span>
                </span>
              </a-link>
            </div>
          </div>
          <div
            class="flex items-center justify-center text-gray-600 text-center text-xs"
            v-if="resent"
          >
            <span class="flex">
              <ion-icon
                name="checkmark"
                class="text-primary-600 h-5 w-5"
              ></ion-icon>
            </span>
            <span class="font-bold ml-1"> A new verification code has been sent successfully. </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import ClientSpinner from "../../ClientSpinner.vue"
import ALink from "../../UI/ALink.vue"
import OtpField from "../components/OtpField.vue"
import VButton from "../../components/VButton.vue"
import Form from "../../../js/helpers/Form"
import Errors from "../../../js/helpers/Errors"

export default {
  name: "PortalMfaAuth",

  components: { ALink, ClientSpinner, VButton, OtpField },

  data() {
    return {
      sending: false,
      loading: false,
      sent: false,
      resent: false,
      form: new Form({
        code: "",
        remember: true,
      }),
      otpValue: "",
      phone: null,
      error: null,
      viaRemember: false,
      step: 1,
      company: window.FinancialCents.company || null,
    }
  },

  mounted() {
    this.loadPhone()
  },

  methods: {
    async send(resent = false) {
      if (this.sending) {
        return
      }

      this.error = null

      this.sending = true
      try {
        await axios.post(`/api/client-portal/mfa/verification-code`)
        this.sent = true
        this.resent = resent
      } catch (e) {
        if (!e.response || e.response.status !== 422) {
          alert("Unable to resend code. Please try refreshing the page.")
        } else if (e.response && e.response.status === 422) {
          this.error = new Errors(e.response.data).toArray().join(" ")
        }
      }
      this.sending = false
    },

    async submit() {
      this.loading = true
      this.error = null
      try {
        const { data } = await this.form.withCredentials().post("/api/client-portal/mfa/verification-code/verify")

        window.location.href = data.data
      } catch (e) {
        console.error(e)
        if (!e.response || e.response.status !== 422) {
          alert("Unable to verify code. Please try again.")
        }
      }
      this.loading = false
    },

    async loadPhone() {
      this.loading = true
      try {
        const { data } = await this.form.get(`/api/client-portal/auth/me`)
        this.account = data.data.account
        this.phone = data.data.phone
        this.viaRemember = data.data.via_remember
        await this.send()
      } catch (e) {
        if (!e.response || e.response.status !== 422) {
          alert("Unable to load account. Please try refreshing the page.")
        }
      }
      this.loading = false
    },

    getPhoneNumber() {
      if (this.phone) {
        return `${this.phone?.country_code.code} ${this.phone.phone}`
      }
    },

    onPaste(code) {
      this.form.code = code
      this.submit()
    },
  },
}
</script>

<style scoped>
.container-sm {
  width: 100%;
  max-width: 500px;
}
</style>
